import React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  ProductDTO,
  ProductSubType,
  ProgramScheduleDTO,
  Role,
  ServicingTrainerType,
  SubscriptionDTO,
  SwapType,
  UpgradeType,
} from "@campgladiator/cg-common.types.types";
import { ButtonProps } from "@campgladiator/cgui-core.atoms.button";
import { isAllowedToChangeTrainerAndSchedule } from "@campgladiator/cg-common.logic.memberships";
import { Paragraph } from "@campgladiator/cgui-core.atoms.typography";
import { TooltipProps } from "@campgladiator/cgui-core.atoms.tooltip";
import { UpgradeSubscriptionArgs } from "@campgladiator/cg-common.api.subscriptions";
import useTermsAndConditions from "../TermsAndConditions/useTermsAndConditions";
import useSelectProduct from "./components/SelectProduct/useSelectProduct";
import { useWithSelectSchedule } from "./components/SelectSchedule/useWithSelectSchedule";
import { SelectScheduleOptions } from "./components/SelectSchedule";
import { SelectTypeProps } from "./components/SelectType";
import { SelectProductProps } from "./components/SelectProduct";
import { filterProgramScheduleByProductSubType } from "./components/SelectSchedule/logic";
import { SelectedTrainer } from "./components/SelectTrainer/SelectTrainer";
import { useUpdateTrainer } from "../../hooks";
import { useUpgradeMembershipModalButtons } from "./";
import { UpgradeMembershipModalProps } from ".";

export type Step =
  | "selectType"
  | "selectSwapType"
  | "selectProduct"
  | "selectTrainer"
  | "selectSchedule"
  | "confirmUpgrade"
  | "termsAndConditions"
  | "upgradeSuccess";

type UseWithUpgradeMembershipModalArgs = {
  areEligibleProductsLoading: boolean;
  currentServicingTrainer: string;
  currentSubscription: SubscriptionDTO;
  currentTrainerName: ServicingTrainerType["name"];
  currentTrainerPhotoUrl: ServicingTrainerType["photoUrl"];
  isCouponStepVisible?: boolean;
  isPriceLoading: boolean;
  isUpgradeLoading: boolean;
  isVisible: boolean;
  membership: ProductDTO[];
  onClose: (success?: boolean) => void;
  onSubmitUpgrade: (args: UpgradeSubscriptionArgs) => Promise<boolean>;
  productName?: string;
  productPrice?: string;
  role: Role;
  stripeSubscriptionId: SubscriptionDTO["stripeSubscriptionId"];
  subscriptionId: SubscriptionDTO["id"];
  subscriptionProductSubType: SubscriptionDTO["productSubType"];
  subscriptionProductType: SubscriptionDTO["productType"];
  theme: ButtonProps["theme"];
  token?: string;
  isSwap?: boolean;
  upgradedSubscriptionId: SubscriptionDTO["id"];
  upgradedSubscriptionProgramName: SubscriptionDTO["productName"];
};

const upgradeCompleteSubtitle =
  "The new subscription will remain pending until the end of the previous contract. Pay attention to the start date.";

const useWithUpgradeMembershipModal = ({
  areEligibleProductsLoading,
  currentServicingTrainer,
  currentTrainerName,
  currentTrainerPhotoUrl,
  currentSubscription,
  isUpgradeLoading,
  isVisible,
  membership,
  productName,
  productPrice,
  subscriptionId,
  subscriptionProductSubType,
  subscriptionProductType,
  onClose,
  onSubmitUpgrade,
  isCouponStepVisible,
  role,
  isPriceLoading,
  token,
  theme,
}: UseWithUpgradeMembershipModalArgs): UpgradeMembershipModalProps => {
  const [step, setStep] = useState<Step>("selectType");
  const [upgradeType, setUpgradeType] = useState<UpgradeType | null>(null);
  const [swapType, setSwapType] = useState<SwapType | null>(null);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>("");
  const [updatedTrainerId, setUpdatedTrainerId] = useState<string | undefined>(
    undefined
  );
  const [updatedTrainerName, setUpdatedTrainerName] = useState<
    string | undefined
  >(undefined);
  const [updatedTrainerPhotoUrl, setUpdatedTrainerPhotoUrl] = useState<
    string | undefined
  >(undefined);
  const [trainerProgramSchedules, setTrainerProgramSchedules] = useState<
    ProgramScheduleDTO[]
  >([]);
  const [selectScheduleOptions, setSelectScheduleOptions] = useState<
    SelectScheduleOptions[]
  >([]);
  const [selectedSchedule, setSelectedSchedule] =
    useState<ProgramScheduleDTO>(undefined);
  const [isTrainerLoading, setIsTrainerLoading] = useState<boolean>(false);
  const hasTrainerGrants = ["TRAINER", "ADMIN", "HQ"].includes(role);
  const [
    isTrainerAndScheduleStepsVisible,
    setIsTrainerAndScheduleStepsVisible,
  ] = useState<boolean>(false);
  const isSwap = swapType === "RESTART";
  const isUpgrade = upgradeType === "UPGRADE";
  const isRecommit = isSwap || isUpgrade;

  const {
    couponOptions,
    productOptions,
    selectedProductOption,
    handleOnSelectNewMembership,
    handleOnSelectCoupon,
    setSelectedProductOption,
    selectedCouponOption,
  } = useSelectProduct({
    membership,
    upgradeType,
    currentSubscription,
    role,
  });

  const selectTrainerProps = {
    ...useUpdateTrainer({
      currentServicingTrainer,
      currentTrainerName,
      currentTrainerPhotoUrl,
      selectedProductOption,
    }),
    productName,
    productPrice,
    setTrainerProgramSchedules,
    setIsTrainerLoading,
    selectedProductOption,
  };

  const { termsUrl } = useTermsAndConditions({
    subscriptionProductType,
    hasTrainerGrants,
  });

  const termsRequired =
    (step === "confirmUpgrade" || step === "termsAndConditions") &&
    !hasAcceptedTerms;
  const { selectedTrainer } = selectTrainerProps;

  const areAnyLoading = isTrainerLoading || isUpgradeLoading;
  const isPrimaryButtonDisabled =
    step === "selectType"
      ? true
      : step === "selectProduct"
      ? !selectedProductOption
      : step === "selectTrainer"
      ? !selectedTrainer || isTrainerLoading
      : step === "selectSchedule"
      ? !selectedSchedule
      : step === "confirmUpgrade"
      ? !selectedProductOption ||
        isUpgradeLoading ||
        (hasTrainerGrants ? false : termsRequired)
      : step === "termsAndConditions"
      ? isUpgradeLoading || termsRequired
      : false;

  const handleOnSubmitUpgrade = useCallback(async () => {
    const status =
      selectedProductOption &&
      (await onSubmitUpgrade({
        subscriptionId,
        productId: selectedProductOption.newProductId,
        productPrice: selectedProductOption.newProductPriceId,
        servicingTrainer: selectedSchedule?.id ? updatedTrainerId : undefined,
        programId: selectedSchedule?.id
          ? selectedSchedule.programId
          : undefined,
        programScheduleId: selectedSchedule?.id,
        couponCode: selectedCouponOption?.id,
        recommit: isRecommit,
        token,
      }));

    return status;
  }, [selectedProductOption, subscriptionId, onSubmitUpgrade]);

  const setRolloverOrRestartCorrectNextStep = (swapType: SwapType) => {
    switch (swapType) {
      case "ROLLOVER":
        if (currentSubscription.productSubType === "IN_PERSON") {
          setStep("confirmUpgrade");
        } else {
          setStep("selectTrainer");
        }
        break;
      case "RESTART":
        setStep("selectProduct");
        break;
    }
  };

  const loadProductForSwapRolloverOrRestartSelection = () => {
    if (!swapType) return;

    const shouldAutoSelectProduct =
      swapType === "ROLLOVER" && upgradeType === "SWAP";

    if (shouldAutoSelectProduct) {
      const productOptionThatMatchesTheCurrentSubscription =
        productOptions.find(
          (opt) => opt.metadata.termLength === currentSubscription.termLength
        );

      if (productOptionThatMatchesTheCurrentSubscription) {
        const { metadata, itemId } =
          productOptionThatMatchesTheCurrentSubscription;
        const [newProductId, newProductPriceId] = itemId.split("|");

        if (newProductId && newProductPriceId) {
          setSelectedProductOption({
            newProductId,
            newProductPriceId,
            productDetails: metadata || {},
          });
        }
      }
    }
    setRolloverOrRestartCorrectNextStep(swapType);
  };

  const handleOnClickViewTerms = () => {
    setStep("termsAndConditions");
  };

  const getModalTitleByStep = (step: Step) => {
    switch (step) {
      case "selectType":
        return "What Changes Would You Like to Make?";
      case "selectSwapType":
        return "Does the Member want to Restart or Rollover their Term?";
      case "selectTrainer":
        return "Select a Trainer";
      case "selectSchedule":
        return "Select A Schedule";
      case "confirmUpgrade":
        return hasTrainerGrants ? "Review Changes" : "Confirm Changes";
      case "termsAndConditions":
        return hasTrainerGrants ? "Bold Big 5" : "Terms And Conditions";
      case "upgradeSuccess":
        return "Your membership has been successfully upgraded!";
      default:
        return upgradeType === "UPGRADE"
          ? "Change Membership Term"
          : "Swap Membership Type";
    }
  };

  const handleOnTrainerUpdate = useCallback(() => {
    const { selectedTrainer } = selectTrainerProps;
    setUpdatedTrainerId(selectedTrainer.itemId || undefined);
    setUpdatedTrainerName(selectedTrainer.text || undefined);
    setUpdatedTrainerPhotoUrl(selectedTrainer.avatar?.src || undefined);
  }, [selectTrainerProps.selectedTrainer]);

  const selectProductProps: SelectProductProps = {
    productOptions,
    couponOptions,
    productName,
    productPrice,
    handleSelectProductOption: handleOnSelectNewMembership,
    handleSelectCouponOption: handleOnSelectCoupon,
    selectedCouponOption,
    selectedProductOption,
    upgradeType,
    isCouponStepVisible,
  };

  const selectTypeProps: SelectTypeProps = {
    productName,
    productPrice,
    isPriceLoading,
  };

  const trainerSelectTooltip =
    "Ask your member which Trainer they plan on working out with most.";
  const scheduleSelectTooltip =
    "Ask your member which Location they plan on attending the most.";
  const confirmationTooltip =
    "Note: Payments made on the current memberships will transfer to the newly created membership.";
  const hasConfirmationTooltip =
    selectProductProps?.upgradeType === "SWAP" &&
    selectProductProps?.selectedProductOption?.productDetails?.termLength ===
      currentSubscription?.termLength;

  const getTooltip = (
    step: string,
    hasConfirmationTooltip: boolean
  ): TooltipProps | null => {
    let tooltipText: string = "";

    switch (step) {
      case "selectTrainer":
        tooltipText = trainerSelectTooltip;
        break;
      case "selectSchedule":
        tooltipText = scheduleSelectTooltip;
        break;
      case "confirmUpgrade":
        tooltipText = hasConfirmationTooltip ? confirmationTooltip : "";
        break;
      default:
        return null;
    }

    return tooltipText
      ? {
          children: (
            <Paragraph size="small" weight="book">
              {tooltipText}
            </Paragraph>
          ),
          arrow: "top",
          style: { zIndex: 9999, "--arrow-size": "10px", minWidth: "300px" },
        }
      : null;
  };

  const tooltip = getTooltip(step, hasConfirmationTooltip);

  const selectSwapTypeProps: SelectTypeProps = {
    isPriceLoading,
  };

  const termsAndConditionsProps = {
    onChangeTermsCheckbox: setHasAcceptedTerms,
    productName,
    productPrice,
    hasTrainerGrants,
    termDocumentUrl: termsUrl,
  };

  const selectScheduleProps = useWithSelectSchedule({
    programSchedules: selectScheduleOptions,
    productName,
    productPrice,
    onSelectedSchedule: setSelectedSchedule,
  });

  const { modalButtons, onCloseModal } = useUpgradeMembershipModalButtons({
    areEligibleProductsLoading,
    upgradeType,
    step,
    setStep,
    setSwapType,
    isTrainerAndScheduleStepsVisible,
    selectScheduleOptions,
    hasTrainerGrants,
    onClose,
    setUpgradeType,
    swapType,
    currentSubscription,
    areAnyLoading,
    isPrimaryButtonDisabled,
    isPriceLoading,
    productName,
    productPrice,
    handleOnSubmitUpgrade,
    theme,
  });

  useEffect(() => {
    loadProductForSwapRolloverOrRestartSelection();
  }, [swapType, productOptions]);

  useEffect(() => {
    const newTitle = getModalTitleByStep(step);
    setModalTitle(newTitle);
  }, [step]);

  useEffect(() => {
    setSelectedProductOption(null);
    setSwapType(null);
    setUpgradeType(null);
    setStep("selectType");
  }, [isVisible]);

  useEffect(() => {
    handleOnTrainerUpdate();
  }, [handleOnTrainerUpdate]);

  useEffect(() => {
    const { selectedTrainer } = selectTrainerProps;
    setTrainerProgramSchedules(selectedTrainer.programSchedules);
  }, [selectTrainerProps.selectedTrainer]);

  useEffect(() => {
    if (selectedProductOption) {
      const selectedSubType = selectedProductOption.productDetails
        .subType as ProductSubType;
      const schedules = filterProgramScheduleByProductSubType(
        trainerProgramSchedules,
        selectedSubType
      )?.map((programSchedule) => {
        return {
          ...programSchedule,
          checked: programSchedule.id === currentSubscription.programScheduleId,
        } as SelectScheduleOptions;
      });
      setSelectScheduleOptions(schedules);
      setIsTrainerAndScheduleStepsVisible(
        isAllowedToChangeTrainerAndSchedule({
          currentSubscription,
          hasTrainerGrants,
          newTermLength: selectedProductOption.productDetails.termLength,
          upgradeType,
        })
      );
    }
  }, [trainerProgramSchedules, selectedProductOption]);

  useEffect(() => {
    if (step === "selectType") {
      const { onItemSelect } = selectTrainerProps;
      onItemSelect({
        itemId: currentServicingTrainer,
        text: currentTrainerName,
        avatar: {
          src: currentTrainerPhotoUrl,
        },
      } as SelectedTrainer);
      setSelectedSchedule(null);
    }
  }, [upgradeType, step]);

  return {
    currentServicingTrainer,
    currentTrainerName,
    currentTrainerPhotoUrl,
    hasTrainerGrants,
    isVisible,
    modalButtons: modalButtons.filter((btn) => !!btn),
    modalTitle,
    productName,
    productPrice,
    selectedCouponOption,
    selectedProductOption,
    selectedSchedule,
    selectProductProps,
    selectScheduleProps,
    selectSwapTypeProps,
    selectTrainerProps,
    selectTypeProps,
    step,
    subscriptionProductSubType,
    subscriptionProductType,
    swapType,
    termsAndConditionsProps,
    updatedTrainerId,
    updatedTrainerName,
    updatedTrainerPhotoUrl,
    tooltip,
    upgradeCompleteSubtitle,
    upgradeType,
    handleOnClickViewTerms,
    onClose: onCloseModal,
    setHasAcceptedTerms,
  };
};

export default useWithUpgradeMembershipModal;
